import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import LocationScheduleEditor from './LocationScheduleEditor';
import { toLocalDateTimeInput, toLocalDateTimeValue, scheduleEndAfterHour } from '../utils/locationSchedule';
import { apiClient } from '../utils/apiClient';
import { extractExpenseReceipt, extractReceipt, mapReceiptToExpense } from '../utils/receiptExtraction';
import ReceiptExtractionStatus from './ReceiptExtractionStatus';
import UserFeatureRequestsDialog from './UserFeatureRequestsDialog';
import PasswordField from './PasswordField';
import LessonHistoryPdf from './LessonHistoryPdf';
import { isMobileDevice, setMobileViewPreference } from '../utils/mobile';
import { userProfilePayload } from '../utils/userPermissions';

const Navigation = () => {
  const { currentUser, setCurrentUser, logout, aircraft, ownedAircraft, people, settings, locations } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState({
    sunday: { startTime: '', endTime: '' },
    monday: { startTime: '', endTime: '' },
    tuesday: { startTime: '', endTime: '' },
    wednesday: { startTime: '', endTime: '' },
    thursday: { startTime: '', endTime: '' },
    friday: { startTime: '', endTime: '' },
    saturday: { startTime: '', endTime: '' }
  });
  const [overrides, setOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ startDate: '', startTime: '', endDate: '', endTime: '', overrideType: 'off' });
  const [editingOverride, setEditingOverride] = useState(null);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showMyInfoDialog, setShowMyInfoDialog] = useState(false);
  const [myInfoActiveTab, setMyInfoActiveTab] = useState('basic');
  const [userInfo, setUserInfo] = useState(null);
  const [userLessons, setUserLessons] = useState([]);
  const [userReservations, setUserReservations] = useState([]);
  const [myPopupQuestionAnswers, setMyPopupQuestionAnswers] = useState([]);
  const [isLoadingMyPopupQuestionAnswers, setIsLoadingMyPopupQuestionAnswers] = useState(false);
  const [myPopupQuestionAnswersError, setMyPopupQuestionAnswersError] = useState('');
  const [editableUserInfo, setEditableUserInfo] = useState(null);
  const [userInfoMessage, setUserInfoMessage] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [showEnlargedProfileImage, setShowEnlargedProfileImage] = useState(false);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({ connected: false, syncEnabled: false, calendarId: 'primary' });
  const [googleCalendarLoading, setGoogleCalendarLoading] = useState(false);
  const [googleCalendarMessage, setGoogleCalendarMessage] = useState('');
  const [googleCalendarMessageType, setGoogleCalendarMessageType] = useState('success');
  const [calendarIdInput, setCalendarIdInput] = useState('primary');
  const [showCalendarIdConfirm, setShowCalendarIdConfirm] = useState(false);
  const [showGoogleCalendarGearOverlay, setShowGoogleCalendarGearOverlay] = useState(false);
  const [showMyLessonsDialog, setShowMyLessonsDialog] = useState(false);
  const [myWorkLessons, setMyWorkLessons] = useState([]);
  const [editingWorkLessonId, setEditingWorkLessonId] = useState(null);
  const [workLessonMessage, setWorkLessonMessage] = useState('');
  const [activeClients, setActiveClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [showMyExpensesDialog, setShowMyExpensesDialog] = useState(false);
  const [myExpenses, setMyExpenses] = useState([]);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseMessage, setExpenseMessage] = useState('');
  const [extractingExpenseIds, setExtractingExpenseIds] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptImage, setSelectedReceiptImage] = useState('');
  const [selectedReceiptType, setSelectedReceiptType] = useState('');
  const [receiptModalMessage, setReceiptModalMessage] = useState('');
  const [isLoadingReceipt, setIsLoadingReceipt] = useState(false);
  const [showMyClientLessonsDialog, setShowMyClientLessonsDialog] = useState(false);
  const [myClientLessons, setMyClientLessons] = useState([]);
  const [myClientUserInfo, setMyClientUserInfo] = useState(null);
  const [showMyEarningsDialog, setShowMyEarningsDialog] = useState(false);
  const [myEarningsStatements, setMyEarningsStatements] = useState([]);
  const [showEarningsDetailsDialog, setShowEarningsDetailsDialog] = useState(false);
  const [selectedEarningsStatement, setSelectedEarningsStatement] = useState(null);
  const [earningsDetailsActiveTab, setEarningsDetailsActiveTab] = useState('workLessons');
  const [earningsWorkLessons, setEarningsWorkLessons] = useState([]);
  const [earningsExpenses, setEarningsExpenses] = useState([]);
  const [showMyProgressDialog, setShowMyProgressDialog] = useState(false);
  const [myProgressData, setMyProgressData] = useState([]);
  const [myProgressCategoryFilter, setMyProgressCategoryFilter] = useState('');
  const [myProgressStageFilter, setMyProgressStageFilter] = useState('');
  const [editingMyProgressId, setEditingMyProgressId] = useState(null);
  const [myProgressMessage, setMyProgressMessage] = useState('');
  const [showUserFeatureRequestsDialog, setShowUserFeatureRequestsDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteAccountMessage, setDeleteAccountMessage] = useState('');
  const userMenuRef = useRef(null);
  const canManageOwnSchedule = currentUser?.user?.orgRole === 'client' || currentUser?.user?.role === 'instructor' || currentUser?.user?.roleId === 3;
  const isClientSchedule = currentUser?.user?.orgRole === 'client';


  React.useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showMyInfoDialog && myInfoActiveTab === 'calendar' && currentUser?.sessionId) {
      fetchGoogleCalendarStatus();
    }
  }, [showMyInfoDialog, myInfoActiveTab, currentUser?.sessionId, currentUser?.user?.orgRole]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const loadUserSchedule = async () => {
    try {
      const userId = currentUser.user?.id || currentUser.id;
      const response = await fetch(`${API_BASE_URL}/work-schedule/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        
        const convertMilitaryToTime = (militaryTime) => {
          if (!militaryTime) return '';
          // If it's already a string in HH:MM format, return as is
          if (typeof militaryTime === 'string' && militaryTime.includes(':')) {
            return militaryTime;
          }
          // If it's a number, convert from military time
          const hours = Math.floor(militaryTime / 100);
          const minutes = militaryTime % 100;
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        };

        const schedule = {
          sunday: { startTime: convertMilitaryToTime(data.sunStart), endTime: convertMilitaryToTime(data.sunEnd) },
          monday: { startTime: convertMilitaryToTime(data.monStart), endTime: convertMilitaryToTime(data.monEnd) },
          tuesday: { startTime: convertMilitaryToTime(data.tueStart), endTime: convertMilitaryToTime(data.tueEnd) },
          wednesday: { startTime: convertMilitaryToTime(data.wedStart), endTime: convertMilitaryToTime(data.wedEnd) },
          thursday: { startTime: convertMilitaryToTime(data.thuStart), endTime: convertMilitaryToTime(data.thuEnd) },
          friday: { startTime: convertMilitaryToTime(data.friStart), endTime: convertMilitaryToTime(data.friEnd) },
          saturday: { startTime: convertMilitaryToTime(data.satStart), endTime: convertMilitaryToTime(data.satEnd) }
        };
        
        setWeeklySchedule(schedule);
        
        const mappedOverrides = (data.scheduleOverrides || []).map(override => ({
          ...override,
          startDateTime: toLocalDateTimeInput(override.startDateTime || override.startDatetime),
          endDateTime: toLocalDateTimeInput(override.endDateTime || override.endDatetime)
        }));
        setOverrides(mappedOverrides);
      }
    } catch (error) {
    }
  };

  const convertTimeToMilitary = (timeStr) => {
    if (!timeStr) return "";
    return timeStr;
  };

  const saveWeeklySchedule = async () => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      if (!currentUser || !userId) {
        setMessage('Error: User not logged in');
        setIsError(true);
        return;
      }
      const payload = {
        userId: userId,
        sunStart: convertTimeToMilitary(weeklySchedule.sunday.startTime),
        sunEnd: convertTimeToMilitary(weeklySchedule.sunday.endTime),
        monStart: convertTimeToMilitary(weeklySchedule.monday.startTime),
        monEnd: convertTimeToMilitary(weeklySchedule.monday.endTime),
        tueStart: convertTimeToMilitary(weeklySchedule.tuesday.startTime),
        tueEnd: convertTimeToMilitary(weeklySchedule.tuesday.endTime),
        wedStart: convertTimeToMilitary(weeklySchedule.wednesday.startTime),
        wedEnd: convertTimeToMilitary(weeklySchedule.wednesday.endTime),
        thuStart: convertTimeToMilitary(weeklySchedule.thursday.startTime),
        thuEnd: convertTimeToMilitary(weeklySchedule.thursday.endTime),
        friStart: convertTimeToMilitary(weeklySchedule.friday.startTime),
        friEnd: convertTimeToMilitary(weeklySchedule.friday.endTime),
        satStart: convertTimeToMilitary(weeklySchedule.saturday.startTime),
        satEnd: convertTimeToMilitary(weeklySchedule.saturday.endTime),
        scheduleOverrides: overrides.map(override => ({
          startDateTime: toLocalDateTimeValue(override.startDateTime),
          endDateTime: toLocalDateTimeValue(override.endDateTime),
          overrideType: override.overrideType
        }))
      };

      const response = await fetch(`${API_BASE_URL}/work-schedule`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setMessage('Weekly schedule saved successfully');
        setIsError(false);
      } else {
        const errorText = await response.text();
        let errorMessage = 'Failed to save weekly schedule';
        try {
          const errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorText || 'Failed to save weekly schedule';
        } catch {
          errorMessage = errorText || 'Failed to save weekly schedule';
        }
        setMessage(`Error: ${errorMessage}`);
        setIsError(true);
      }
    } catch (error) {
      setMessage('Error: Error saving schedule');
      setIsError(true);
    }
  };

  const addOverride = () => {
    if (!newOverride.startDate || !newOverride.endDate) return;
    
    const startTime = newOverride.startTime || '00:00';
    const endTime = newOverride.endTime || '23:30';
    
    // Create local datetime strings without timezone conversion
    const startDateTime = `${newOverride.startDate}T${startTime}:00`;
    const endDateTime = `${newOverride.endDate}T${endTime}:00`;
    
    setOverrides(prev => [...prev, {
      startDateTime,
      endDateTime,
      overrideType: newOverride.overrideType
    }]);
    setNewOverride({ startDate: '', startTime: '', endDate: '', endTime: '', overrideType: 'off' });
  };

  const removeOverride = (index) => {
    setOverrides(overrides.filter((_, i) => i !== index));
  };

  const updateOverride = (index, field, value) => {
    const updated = overrides.map((override, i) => {
      if (i === index) {
        if (field === 'startDate' || field === 'startTime') {
          const startDate = field === 'startDate' ? value : (override.startDateTime ? override.startDateTime.split('T')[0] : '');
          const startTime = field === 'startTime' ? value : (override.startDateTime && override.startDateTime.split('T')[1] ? override.startDateTime.split('T')[1].slice(0, 5) : '00:00');
          return { ...override, startDateTime: `${startDate}T${startTime}:00` };
        } else if (field === 'endDate' || field === 'endTime') {
          const endDate = field === 'endDate' ? value : (override.endDateTime ? override.endDateTime.split('T')[0] : '');
          const endTime = field === 'endTime' ? value : (override.endDateTime && override.endDateTime.split('T')[1] ? override.endDateTime.split('T')[1].slice(0, 5) : '23:30');
          return { ...override, endDateTime: `${endDate}T${endTime}:00` };
        } else {
          return { ...override, [field]: value };
        }
      }
      return override;
    });
    setOverrides(updated);
  };

  React.useEffect(() => {
    if (showScheduleDialog && currentUser) {
      loadUserSchedule();
    }
  }, [showScheduleDialog, currentUser]);

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const militaryTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const displayTime = `${h12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        options.push({ value: militaryTime, display: displayTime });
      }
    }
    return options;
  };

  const openScheduleDialog = () => {
    setShowUserMenu(false);
    setShowScheduleDialog(true);
    setMessage('');
  };

  const loadUserInfo = async () => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      const userResponse = await fetch(`${API_BASE_URL}/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUserInfo(userData);
        setEditableUserInfo(userData);
        setProfileImageFile(null);
        setProfileImagePreview(userData.profileImageUrl || '');
      }
      
      const lessonsResponse = await fetch(`${API_BASE_URL}/lesson/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (lessonsResponse.ok) {
        const lessonsData = await lessonsResponse.json();
        setUserLessons(lessonsData);
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const loadUserReservations = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/reservation/user/${userId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setUserReservations(data);
      } else {
        setUserReservations([]);
      }
    } catch (error) {
      console.error('Error loading user reservations:', error);
      setUserReservations([]);
    }
  };

  const loadMyPopupQuestionAnswers = async () => {
    if (!currentUser?.sessionId) return;
    setIsLoadingMyPopupQuestionAnswers(true);
    setMyPopupQuestionAnswersError('');
    try {
      const response = await fetch(`${API_BASE_URL}/popup-question/answers`, {
        headers: { 'session-id': currentUser.sessionId }
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || 'Unable to load answered questions');
      }
      setMyPopupQuestionAnswers(Array.isArray(data) ? data : []);
    } catch (error) {
      setMyPopupQuestionAnswers([]);
      setMyPopupQuestionAnswersError(error.message);
    } finally {
      setIsLoadingMyPopupQuestionAnswers(false);
    }
  };

  const formatReservationTime = (militaryTime) => {
    if (militaryTime === null || militaryTime === undefined || militaryTime === '') return '';
    const hours = Math.floor(militaryTime / 100);
    const minutes = militaryTime % 100;
    const h12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const loadActiveClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clients/active`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveClients(data);
      }
    } catch (error) {
      console.error('Error loading active clients:', error);
    }
  };

  const loadAllClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clients/all`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllClients(data);
      } else {
        console.error('Failed to load all clients:', await response.text());
      }
    } catch (error) {
      console.error('Error loading all clients:', error);
    }
  };

  const loadMyWorkLessons = async () => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      const response = await fetch(`${API_BASE_URL}/work-lesson/instructor/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyWorkLessons(data);
      }
    } catch (error) {
      console.error('Error loading my work lessons:', error);
    }
  };

  const getClientName = (clientId, fallbackName = '') => {
    if (!clientId) return fallbackName;
    const client = allClients.find(c => c.id == clientId);
    return client ? `${client.firstName} ${client.lastName}` : fallbackName;
  };

  const saveMyWorkLesson = async (lessonData) => {
    try {
      const payload = {
        id: lessonData.id,
        instructorId: currentUser?.user?.id || currentUser?.id,
        clientId: lessonData.clientId || null,
        date: lessonData.date,
        startTime: lessonData.startTime,
        clientName: lessonData.clientName,
        dualGiven: parseFloat(lessonData.dualGiven) || 0,
        groundInstruction: parseFloat(lessonData.groundInstruction) || 0,
        total: parseFloat(lessonData.total) || 0
      };
      
      const response = await fetch(`${API_BASE_URL}/work-lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setWorkLessonMessage('Lesson saved successfully');
        loadMyWorkLessons();
        setEditingWorkLessonId(null);
        setTimeout(() => setWorkLessonMessage(''), 3000);
      } else {
        const errorText = await response.text();
        console.error('Save error:', errorText);
        setWorkLessonMessage(`Error: ${errorText}`);
        setTimeout(() => setWorkLessonMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error saving work lesson:', error);
      setWorkLessonMessage(`Error: ${error.message}`);
      setTimeout(() => setWorkLessonMessage(''), 5000);
    }
  };

  const updateWorkLessonField = (lessonId, field, value) => {
    setMyWorkLessons(prev => prev.map(lesson => 
      lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
    ));
  };

  const loadMyExpenses = async () => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      const response = await fetch(`${API_BASE_URL}/expense/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyExpenses(data);
      }
    } catch (error) {
      console.error('Error loading my expenses:', error);
    }
  };

  const saveMyExpense = async (expenseData) => {
    try {
      const formData = new FormData();
      
      if (expenseData.id && !expenseData.id.toString().startsWith('temp_')) {
        formData.append('id', expenseData.id);
      }
      formData.append('date', expenseData.date);
      formData.append('expense', expenseData.expense);
      formData.append('vendor', expenseData.vendor);
      formData.append('details', expenseData.details || '');
      formData.append('total', parseFloat(expenseData.total) || 0);
      formData.append('userId', currentUser?.user?.id || currentUser?.id);
      if (expenseData.aircraftId) {
        formData.append('aircraftId', expenseData.aircraftId);
      }
      if (expenseData.earningStatementId) {
        formData.append('earningStatementId', expenseData.earningStatementId);
      }
      if (expenseData.receiptFile) {
        formData.append('receiptImage', expenseData.receiptFile);
      }
      
      const response = await fetch(`${API_BASE_URL}/expense`, {
        method: 'POST',
        headers: {
          'session-id': currentUser?.sessionId
        },
        body: formData
      });
      
      if (response.ok) {
        setExpenseMessage('Expense saved successfully');
        loadMyExpenses();
        setEditingExpenseId(null);
        setTimeout(() => setExpenseMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setExpenseMessage(`Error: ${errorText}`);
        setTimeout(() => setExpenseMessage(''), 5000);
      }
    } catch (error) {
      setExpenseMessage(`Error: ${error.message}`);
      setTimeout(() => setExpenseMessage(''), 5000);
    }
  };

  const updateMyExpenseField = async (expenseId, field, value) => {
    if (field === 'receiptFile' && value) {
      if (value.size > 1024 * 1024) {
        setExpenseMessage('Error: File size must be under 1MB');
        setTimeout(() => setExpenseMessage(''), 5000);
        return;
      }
    }
    setMyExpenses(prev => prev.map(expense => 
      expense.id === expenseId ? { ...expense, [field]: value } : expense
    ));

    if (field === 'receiptFile' && value) {
      setExtractingExpenseIds(prev => [...new Set([...prev, expenseId])]);
      try {
        const extractedFields = await extractExpenseReceipt(value, currentUser?.sessionId, aircraft, settings?.airports);
        if (extractedFields) {
          setMyExpenses(prev => prev.map(expense => (
            expense.id === expenseId && expense.receiptFile === value
              ? { ...expense, ...extractedFields }
              : expense
          )));
        }
      } catch (error) {
        setExpenseMessage(`Receipt extraction failed: ${error.message}`);
        setTimeout(() => setExpenseMessage(''), 5000);
      } finally {
        setExtractingExpenseIds(prev => prev.filter(id => id !== expenseId));
      }
    }
  };

  const openMyExpenseReceipt = async (expense) => {
    setShowReceiptModal(true);
    setReceiptModalMessage('');
    setSelectedReceiptImage('');
    setSelectedReceiptType('');
    setIsLoadingReceipt(true);

    const receiptUrl = typeof expense.receiptImage === 'string' ? expense.receiptImage.trim() : '';
    if (receiptUrl) {
      const resolvedUrl = /^(https?:\/\/|data:|blob:)/i.test(receiptUrl)
        ? receiptUrl
        : `${API_BASE_URL}/${receiptUrl.replace(/^\//, '')}`;
      setSelectedReceiptImage(resolvedUrl);
      setSelectedReceiptType(/\.pdf(?:$|[?#])/i.test(receiptUrl) ? 'pdf' : 'image');
      setIsLoadingReceipt(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/expense/${expense.id}/image`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || `Unable to load receipt (${response.status})`);
      }
      const blob = await response.blob();
      setSelectedReceiptImage(URL.createObjectURL(blob));
      setSelectedReceiptType(blob.type === 'application/pdf' ? 'pdf' : 'image');
    } catch (error) {
      setReceiptModalMessage(error.message || 'Unable to load receipt');
    } finally {
      setIsLoadingReceipt(false);
    }
  };

  const closeReceiptModal = () => {
    if (selectedReceiptImage.startsWith('blob:')) URL.revokeObjectURL(selectedReceiptImage);
    setShowReceiptModal(false);
    setSelectedReceiptImage('');
    setSelectedReceiptType('');
    setReceiptModalMessage('');
    setIsLoadingReceipt(false);
  };

  const handleMyExpenseReceiptChange = async (expenseId, file) => {
    if (!file) return;
    if (file.size > 1048576) {
      updateMyExpenseField(expenseId, 'receiptFile', file);
      return;
    }
    updateMyExpenseField(expenseId, 'receiptFile', file);
    if (!file.type.startsWith('image/')) return;

    setExpenseMessage('Scanning receipt...');
    try {
      const result = await extractReceipt(file, currentUser?.sessionId);
      const extractedFields = mapReceiptToExpense(result, aircraft);
      setMyExpenses(prev => prev.map(expense => (
        expense.id === expenseId && expense.receiptFile === file
          ? { ...expense, ...extractedFields }
          : expense
      )));
      setExpenseMessage('Receipt scanned. Review the extracted fields before saving.');
    } catch (error) {
      setExpenseMessage(`Error: ${error.message}`);
    }
  };

  const deleteMyExpense = async (expenseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/expense/${expenseId}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        loadMyExpenses();
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const addNewMyExpense = () => {
    const tempId = `temp_${Date.now()}`;
    const newExpense = {
      id: tempId,
      date: new Date().toISOString().split('T')[0],
      expense: '',
      vendor: '',
      details: '',
      total: '',
      isAttachment: false,
      userId: currentUser?.user?.id || currentUser?.id,
      earningStatementId: null
    };
    setMyExpenses([newExpense, ...myExpenses]);
    setEditingExpenseId(tempId);
  };

  const loadMyClientLessons = async () => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      const userResponse = await fetch(`${API_BASE_URL}/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setMyClientUserInfo(userData);
      }
      
      const lessonsResponse = await fetch(`${API_BASE_URL}/lesson/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (lessonsResponse.ok) {
        const lessonsData = await lessonsResponse.json();
        setMyClientLessons(lessonsData);
      }
    } catch (error) {
      console.error('Error loading my client lessons:', error);
    }
  };

  const loadMyEarningsStatements = async () => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      const response = await fetch(`${API_BASE_URL}/earnings-statement/list/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMyEarningsStatements(data);
      }
    } catch (error) {
      console.error('Error loading my earnings statements:', error);
    }
  };

  const loadEarningsStatementItems = async (earningsStatementId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/items/${earningsStatementId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEarningsWorkLessons(data.workLessons || []);
        setEarningsExpenses(data.expenses || []);
      }
    } catch (error) {
      console.error('Error loading earnings statement items:', error);
    }
  };

  const handleEarningsStatementClick = (earningsStatement) => {
    setSelectedEarningsStatement(earningsStatement);
    setEarningsDetailsActiveTab('workLessons');
    loadEarningsStatementItems(earningsStatement.id);
    setShowEarningsDetailsDialog(true);
  };

  const loadMyProgressData = async () => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      const response = await fetch(`${API_BASE_URL}/requirement/user/${userId}`, {
        method: 'GET',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const records = Array.isArray(data) ? data : [];
        setMyProgressData(records);
        const category = getDefaultMyProgressCategory(records);
        setMyProgressCategoryFilter(category);
        setMyProgressStageFilter(getMyProgressStages(category, records)[0] || '');
      } else {
        setMyProgressData([]);
        setMyProgressCategoryFilter('');
        setMyProgressStageFilter('');
      }
    } catch (error) {
      setMyProgressData([]);
      setMyProgressCategoryFilter('');
      setMyProgressStageFilter('');
    }
  };

  const myProgressCategoryOrder = ['private pilot', 'instrument rating', 'commercial pilot', 'multi-engine', 'multi-engine rating'];
  const getMyProgressCategories = (records = myProgressData) => [...new Set((Array.isArray(records) ? records : []).map((item) => (item.category || '').trim()).filter(Boolean))].sort((a, b) => {
    const aIndex = myProgressCategoryOrder.indexOf(a.toLowerCase());
    const bIndex = myProgressCategoryOrder.indexOf(b.toLowerCase());
    if (aIndex !== -1 || bIndex !== -1) return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
    return a.localeCompare(b);
  });

  const getMyProgressItems = (category, records = myProgressData) => (Array.isArray(records) ? records : []).filter((item) => !category || (item.category || '').trim() === category);
  const getMyProgressStages = (category, records = myProgressData) => [...new Set(getMyProgressItems(category, records).map((item) => (item.stage || '').trim()).filter(Boolean))];

  const getDefaultMyProgressCategory = (records) => {
    const categories = getMyProgressCategories(records);
    const normalizeName = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const findCategory = (...names) => categories.find((category) => names.some((name) => normalizeName(category) === normalizeName(name)));
    const certifications = `${currentUser?.user?.certifications || currentUser?.certifications || ''}`.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
    const hasPrivate = certifications.includes('private pilot');
    const hasInstrument = certifications.includes('instrument rating');
    const hasCommercial = certifications.includes('commercial pilot');
    const hasCfi = certifications.includes('cfi');

    if (hasCommercial && (hasInstrument || hasCfi)) {
      const multiEngine = findCategory('Multi-Engine', 'Multi Engine', 'Multi-Engine Rating', 'Multi Engine Rating') || categories.find((category) => normalizeName(category).includes('multiengine'));
      if (multiEngine) return multiEngine;
    }
    if (hasInstrument) {
      const commercial = findCategory('Commercial Pilot');
      if (commercial) return commercial;
    }
    if (hasPrivate) {
      const instrument = findCategory('Instrument Rating');
      if (instrument) return instrument;
    }
    return findCategory('Private Pilot') || categories[0] || '';
  };

  const selectMyProgressCategory = (category) => {
    setMyProgressCategoryFilter(category);
    setMyProgressStageFilter(getMyProgressStages(category)[0] || '');
  };

  const myProgressCategories = getMyProgressCategories();
  const myProgressStages = getMyProgressStages(myProgressCategoryFilter);
  const filteredMyProgressData = getMyProgressItems(myProgressCategoryFilter).filter((item) => !myProgressStageFilter || (item.stage || '').trim() === myProgressStageFilter);

  const updateMyProgressField = (itemId, field, value) => {
    setMyProgressData(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const saveMyProgressItem = async (item) => {
    try {
      const userId = currentUser?.user?.id || currentUser?.id;
      const payload = [{
        userId: parseInt(userId),
        requirementId: item.id,
        status: item.status,
        level: item.level
      }];
      
      const response = await fetch(`${API_BASE_URL}/requirement/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setMyProgressMessage('Progress updated successfully');
        setEditingMyProgressId(null);
        setTimeout(() => setMyProgressMessage(''), 3000);
      } else {
        setMyProgressMessage('Error: Failed to update progress');
        setTimeout(() => setMyProgressMessage(''), 5000);
      }
    } catch (error) {
      setMyProgressMessage(`Error: ${error.message}`);
      setTimeout(() => setMyProgressMessage(''), 5000);
    }
  };

  const saveUserInfo = async () => {
    try {
      const payload = userProfilePayload(editableUserInfo, currentUser?.user?.orgRole);
      let requestBody = JSON.stringify(payload);
      const requestHeaders = { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId };
      if (profileImageFile) {
        const multipartData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null || key === 'profileImageUrl') return;
          const serializedValue = key === 'aircraftList'
            ? JSON.stringify(value)
            : ['isInstructor', 'isReferralRestricted'].includes(key)
              ? String(value === true || value === 1 || value === '1' || value === 'true')
              : value;
          multipartData.append(key, serializedValue);
        });
        multipartData.append('profileImage', profileImageFile);
        requestBody = multipartData;
        delete requestHeaders['Content-Type'];
      }
      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody
      });
      
      if (response.ok) {
        const result = await response.json();
        const profileImageUrl = result.profileImageUrl || editableUserInfo.profileImageUrl || profileImagePreview;
        setUserInfoMessage('User information saved successfully');
        setUserInfo({ ...editableUserInfo, profileImageUrl });
        setEditableUserInfo(prev => ({ ...prev, profileImageUrl }));
        setProfileImagePreview(profileImageUrl);
        setProfileImageFile(null);
        setCurrentUser(prev => prev ? { ...prev, user: { ...(prev.user || {}), ...editableUserInfo, profileImageUrl } } : prev);
        setTimeout(() => setUserInfoMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setUserInfoMessage(`Error: ${errorText}`);
      }
    } catch (error) {
      setUserInfoMessage(`Error: ${error.message}`);
    }
  };

  const selectProfileImage = (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setUserInfoMessage('Error: Profile image must be PNG or JPEG');
      return;
    }
    if (file.size > 1024 * 1024) {
      setUserInfoMessage('Error: Profile image must be 1 MB or smaller');
      return;
    }
    if (profileImagePreview.startsWith('blob:')) URL.revokeObjectURL(profileImagePreview);
    setProfileImageFile(file);
    setProfileImagePreview(URL.createObjectURL(file));
    setUserInfoMessage('');
  };

  const fetchGoogleCalendarStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/google/calendar/status`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        const status = {
          connected: Boolean(data?.connected),
          syncEnabled: Boolean(data?.syncEnabled),
          calendarId: data?.calendarId || 'primary'
        };
        setGoogleCalendarStatus(status);
        setCalendarIdInput(status.calendarId);
      } else {
        setGoogleCalendarStatus({ connected: false, syncEnabled: false, calendarId: 'primary' });
      }
    } catch (error) {
      setGoogleCalendarStatus({ connected: false, syncEnabled: false, calendarId: 'primary' });
    }
  };

  const handleGoogleCalendarConnect = async () => {
    setGoogleCalendarLoading(true);
    setGoogleCalendarMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/google/calendar/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.url) {
          window.open(data.url, '_blank', 'noopener,noreferrer');
          setGoogleCalendarMessage('Complete the connection in the new tab, then return here and refresh status. Afterwards, click "Sync Now" to sync all your past and future reservations with Google Calendar.');
          setGoogleCalendarMessageType('success');
          return;
        }
        setGoogleCalendarMessage('Google Calendar connection flow started.');
        setGoogleCalendarMessageType('success');
      } else {
        const errorData = await response.json().catch(() => null);
        setGoogleCalendarMessage(errorData?.error || 'Unable to start Google Calendar connection.');
        setGoogleCalendarMessageType('error');
      }
    } catch (error) {
      setGoogleCalendarMessage(error?.message || 'Unable to start Google Calendar connection.');
      setGoogleCalendarMessageType('error');
    } finally {
      setGoogleCalendarLoading(false);
    }
  };

  const handleGoogleCalendarDisconnect = async () => {
    setGoogleCalendarLoading(true);
    setShowGoogleCalendarGearOverlay(true);
    setGoogleCalendarMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/google/calendar/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        setGoogleCalendarMessage('Google Calendar disconnected.');
        setGoogleCalendarMessageType('success');
        await fetchGoogleCalendarStatus();
      } else {
        const errorData = await response.json().catch(() => null);
        setGoogleCalendarMessage(errorData?.error || 'Unable to disconnect Google Calendar.');
        setGoogleCalendarMessageType('error');
      }
    } catch (error) {
      setGoogleCalendarMessage(error?.message || 'Unable to disconnect Google Calendar.');
      setGoogleCalendarMessageType('error');
    } finally {
      setGoogleCalendarLoading(false);
      setShowGoogleCalendarGearOverlay(false);
    }
  };

  const handleGoogleCalendarToggle = async (syncEnabled) => {
    setGoogleCalendarLoading(true);
    setGoogleCalendarMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/google/calendar/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ syncEnabled })
      });

      if (response.ok) {
        setGoogleCalendarMessage(syncEnabled ? 'Google Calendar sync enabled.' : 'Google Calendar sync disabled.');
        setGoogleCalendarMessageType('success');
        await fetchGoogleCalendarStatus();
      } else {
        const errorData = await response.json().catch(() => null);
        setGoogleCalendarMessage(errorData?.error || 'Unable to update Google Calendar sync settings.');
        setGoogleCalendarMessageType('error');
      }
    } catch (error) {
      setGoogleCalendarMessage(error?.message || 'Unable to update Google Calendar sync settings.');
      setGoogleCalendarMessageType('error');
    } finally {
      setGoogleCalendarLoading(false);
    }
  };

  const handleGoogleCalendarSaveCalendarId = async () => {
    setGoogleCalendarLoading(true);
    setGoogleCalendarMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/google/calendar/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ calendarId: calendarIdInput || null })
      });

      if (response.ok) {
        setGoogleCalendarMessage('Google Calendar target updated.');
        setGoogleCalendarMessageType('success');
        await fetchGoogleCalendarStatus();
      } else {
        const errorData = await response.json().catch(() => null);
        setGoogleCalendarMessage(errorData?.error || 'Unable to update Google Calendar target.');
        setGoogleCalendarMessageType('error');
      }
    } catch (error) {
      setGoogleCalendarMessage(error?.message || 'Unable to update Google Calendar target.');
      setGoogleCalendarMessageType('error');
    } finally {
      setGoogleCalendarLoading(false);
    }
  };

  const handleGoogleCalendarSaveCalendarIdClick = () => {
    const trimmedCalendarId = (calendarIdInput || '').trim();
    if (!trimmedCalendarId || trimmedCalendarId.toLowerCase() === 'primary') {
      setCalendarIdInput('primary');
      handleGoogleCalendarSaveCalendarId();
      return;
    }
    setShowCalendarIdConfirm(true);
  };

  const confirmGoogleCalendarSaveCalendarId = () => {
    setShowCalendarIdConfirm(false);
    handleGoogleCalendarSaveCalendarId();
  };

  const cancelGoogleCalendarSaveCalendarId = () => {
    setShowCalendarIdConfirm(false);
    setCalendarIdInput(googleCalendarStatus.calendarId || 'primary');
  };

  const handleGoogleCalendarSyncNow = async () => {
    setGoogleCalendarLoading(true);
    setShowGoogleCalendarGearOverlay(true);
    setGoogleCalendarMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/google/calendar/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        setGoogleCalendarMessage('Google Calendar sync completed.');
        setGoogleCalendarMessageType('success');
      } else {
        const errorData = await response.json().catch(() => null);
        setGoogleCalendarMessage(errorData?.error || 'Unable to start Google Calendar sync.');
        setGoogleCalendarMessageType('error');
      }
    } catch (error) {
      setGoogleCalendarMessage(error?.message || 'Unable to start Google Calendar sync.');
      setGoogleCalendarMessageType('error');
    } finally {
      setGoogleCalendarLoading(false);
      setShowGoogleCalendarGearOverlay(false);
    }
  };

  const validatePassword = (password) => {
    const minLength = 10;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (password.length < minLength) {
      return 'Password must be at least 10 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)';
    }
    return null;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    setPasswordError(false);
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage('New password and confirm password do not match');
      setPasswordError(true);
      return;
    }
    
    // Password validation
    const passwordError = validatePassword(passwordData.newPassword);
    if (passwordError) {
      setPasswordMessage(passwordError);
      setPasswordError(true);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/user/passwordchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({
          userId: currentUser?.user?.id || currentUser?.id,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });
      
      if (response.ok) {
        setPasswordMessage('Password changed successfully');
        setPasswordError(false);
        setTimeout(() => {
          setShowPasswordDialog(false);
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setPasswordMessage('');
        }, 3000);
      } else {
        const errorText = await response.text();
        let errorMessage = 'Failed to change password';
        try {
          const errorObj = JSON.parse(errorText);
          errorMessage = errorObj.error || errorText || 'Failed to change password';
        } catch {
          errorMessage = errorText || 'Failed to change password';
        }
        setPasswordMessage(errorMessage);
        setPasswordError(true);
      }
    } catch (error) {
      setPasswordMessage('Error changing password');
      setPasswordError(true);
    }
  };

  const getMenuItemStyle = (path) => ({
    textDecoration: 'none',
    color: '#fffacd',
    padding: '0.75rem 1.5rem',
    borderRadius: '25px',
    backgroundColor: location.pathname === path ? '#87ceeb' : 'rgba(255,255,255,0.1)',
    transition: 'all 0.3s ease',
    border: '2px solid #fffacd',
    fontWeight: 'bold'
  });
  

  return (
    <>
    {showEnlargedProfileImage && profileImagePreview && (
      <div onClick={() => setShowEnlargedProfileImage(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000, cursor: 'zoom-out' }}>
        <div onClick={(event) => event.stopPropagation()} style={{ position: 'relative', backgroundColor: 'white', padding: '1rem', borderRadius: '8px', maxWidth: '92vw', maxHeight: '92vh' }}>
          <button type="button" onClick={() => setShowEnlargedProfileImage(false)} style={{ position: 'absolute', top: '0.25rem', right: '0.25rem', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '18px', zIndex: 1 }}>×</button>
          <img src={profileImagePreview} alt="Profile" style={{ maxWidth: '88vw', maxHeight: '85vh', objectFit: 'contain', display: 'block' }} />
        </div>
      </div>
    )}
    {showCalendarIdConfirm && (
      <div onClick={cancelGoogleCalendarSaveCalendarId} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000 }}>
        <div onClick={(event) => event.stopPropagation()} style={{ backgroundColor: 'white', padding: '1.75rem', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', maxWidth: '460px', width: '90%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '1.75rem' }}>⚠️</span>
            <h3 style={{ margin: 0 }}>Confirm Custom Calendar ID</h3>
          </div>
          <p style={{ marginTop: 0, lineHeight: '1.5' }}>
            You're changing where reservations sync to: <strong>{calendarIdInput}</strong>
          </p>
          <p style={{ lineHeight: '1.5' }}>Before saving, make sure:</p>
          <ul style={{ lineHeight: '1.6', paddingLeft: '1.25rem' }}>
            <li>You already have a calendar with this exact ID in your Google account (Google Calendar → Settings → your calendar → "Integrate calendar" shows its Calendar ID).</li>
            <li>You've copied the ID exactly as shown by Google — it's case-sensitive and usually ends in <code>@group.calendar.google.com</code> for secondary calendars.</li>
            <li>The Google account you connected has edit access to that calendar.</li>
          </ul>
          <p style={{ lineHeight: '1.5', color: '#6c757d' }}>
            If you're not sure, leave this set to <strong>primary</strong> to sync with your main Google Calendar.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.25rem' }}>
            <button onClick={confirmGoogleCalendarSaveCalendarId} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
              Save Anyway
            </button>
            <button onClick={cancelGoogleCalendarSaveCalendarId} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    <nav style={{ height: '92px', boxSizing: 'border-box', padding: '0 2rem', background: 'url(https://storage.googleapis.com/realair-images/sky-background.jpg) center/cover, linear-gradient(135deg, #87CEEB 0%, #98D8E8 50%, #B0E0E6 100%)', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src={`${process.env.PUBLIC_URL}/Real_AIr_Scheduling_logo.png`} alt="Real Air Scheduling" style={{ width: '92px', height: '92px', flex: '0 0 92px', objectFit: 'contain' }} />
          <h1 style={{ margin: 0, fontSize: '2.424rem', color: '#fffacd', fontWeight: '600' }}>Real Air Scheduling</h1>
        </div>
        <div className="scheduling-header-controls" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div className="scheduling-header-links" aria-label="Main navigation links" tabIndex={0} style={{ display: 'contents' }}>
          {currentUser?.user?.status !== 'limited' && <Link to="/calendar" style={getMenuItemStyle('/calendar')}>Flight Schedule</Link>}
          <Link to="/events" style={getMenuItemStyle('/events')}>Events</Link>
          <Link to="/training" style={getMenuItemStyle('/training')}>Training</Link>
          {currentUser?.user?.orgRole !== 'client' && <Link to="/people" style={getMenuItemStyle('/people')}>People</Link>}
          {(currentUser?.user?.orgRole !== 'client' || (currentUser?.user?.orgRole === 'client' && ownedAircraft?.length > 0)) && <Link to="/aircraft" style={getMenuItemStyle('/aircraft')}>Aircraft</Link>}
          {['admin', 'superuser', 'instructor'].includes(currentUser?.user?.orgRole) && <Link to="/manage" style={getMenuItemStyle('/manage')}>Manage</Link>}
          </div>
          <div className="scheduling-user-menu" style={{ position: 'relative' }} ref={userMenuRef}>
            <button aria-label="User menu" aria-expanded={showUserMenu} onClick={() => setShowUserMenu(!showUserMenu)} style={{ color: '#fffacd', padding: '0.5rem', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', border: '2px solid #fffacd', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '60px', height: '60px', justifyContent: 'center', fontWeight: 'bold' }}>
              {currentUser?.user?.profileImageUrl ? (
                <img src={currentUser.user.profileImageUrl} alt="Profile" style={{ width: '30px', height: '30px', borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.7)' }} />
              ) : (
                <div style={{ fontSize: '1.2rem' }}>👤</div>
              )}
              <div style={{ fontSize: '0.6rem', marginTop: '2px' }}>{currentUser?.user?.firstName || currentUser?.firstName || 'User'}</div>
            </button>
            {showUserMenu && (
              <div style={{ position: 'absolute', top: '100%', right: 0, backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1000, minWidth: '150px' }}>
                <button onClick={() => { setShowUserMenu(false); setShowMyInfoDialog(true); loadUserInfo(); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer' }}>My Info</button>
                {(currentUser?.user?.role === 'instructor' || currentUser?.user?.roleId === 3) && <button onClick={() => { setShowUserMenu(false); setShowMyLessonsDialog(true); loadActiveClients(); loadAllClients(); loadMyWorkLessons(); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>My Lessons</button>}
                {currentUser?.user?.orgRole === 'client' && <button onClick={() => { setShowUserMenu(false); setShowMyClientLessonsDialog(true); loadMyClientLessons(); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>My Lessons</button>}
                <button onClick={() => { setShowUserMenu(false); setShowMyProgressDialog(true); loadMyProgressData(); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>My Progress</button>
                <button onClick={() => { setShowUserMenu(false); setShowUserFeatureRequestsDialog(true); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>My Feature Requests</button>
                {(['superuser', 'admin', 'staff', 'member'].includes(currentUser?.user?.orgRole) || currentUser?.user?.roleId === 3) && <button onClick={() => { setShowUserMenu(false); setShowMyExpensesDialog(true); loadMyExpenses(); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>My Expenses</button>}
                {(currentUser?.user?.orgRole === 'staff' || currentUser?.user?.roleId === 3) && <button onClick={() => { setShowUserMenu(false); setShowMyEarningsDialog(true); loadMyEarningsStatements(); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>My Earnings Statements</button>}
                {canManageOwnSchedule && <button onClick={openScheduleDialog} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>My Schedule</button>}
                {isMobileDevice() && <button onClick={() => { setShowUserMenu(false); setMobileViewPreference('mobile'); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>View Mobile Site</button>}
                <button onClick={() => { setShowUserMenu(false); setShowPasswordDialog(true); setPasswordMessage(''); setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' }); }} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>Change My Password</button>
                <button onClick={handleLogout} style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', border: 'none', backgroundColor: 'transparent', textAlign: 'left', cursor: 'pointer', borderTop: '1px solid #eee' }}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {showScheduleDialog && locations.length > 0 && (
        <div onMouseDown={event => { if (event.target === event.currentTarget) setShowScheduleDialog(false); }} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div className="my-schedule-dialog" role="dialog" aria-modal="true" aria-label="Edit My Schedule" style={{ position: 'relative', padding: '1.5rem', width: 'min(850px, 95%)', height: 'min(850px, 90vh)', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', borderRadius: '8px' }}>
            <button type="button" title="Close" aria-label="Close Edit My Schedule" onClick={() => setShowScheduleDialog(false)} style={{ position: 'absolute', right: '1rem', top: '1rem', background: 'none', border: 0, fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            <h3 className="my-schedule-title" style={{ paddingRight: '3.5rem', flexShrink: 0 }}>Edit My Schedule</h3>
            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>
              <LocationScheduleEditor fixedFooter resourceId={currentUser?.user?.id || currentUser?.id} baseRate={currentUser?.user?.rate} />
            </div>
          </div>
        </div>
      )}
      {showScheduleDialog && locations.length === 0 && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div className="my-schedule-dialog" style={{ position: 'relative', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '600px', width: '90%', maxHeight: 'calc(100vh - 2rem)', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, paddingRight: '2rem' }}>Edit My Schedule</h3>
              <button type="button" aria-label="Close Edit My Schedule" title="Close" onClick={() => setShowScheduleDialog(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', border: 'none', background: 'transparent', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer', padding: '0.25rem' }}>×</button>
            </div>

            {isClientSchedule && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '6px', backgroundColor: '#e7f3ff', border: '1px solid #b6dcff', color: '#1f4f7a', fontSize: '0.9rem' }}>
                Set when you are available for flight training or to act as a safety pilot.
              </div>
            )}
            
            <div style={{ marginBottom: '1rem', flexShrink: 0 }}>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>Weekly Schedule</h4>
              {Object.entries(weeklySchedule).map(([day, times]) => (
                <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <label style={{ width: '100px', textTransform: 'capitalize' }}>{day}:</label>
                  <select value={times.startTime} onChange={(e) => setWeeklySchedule({...weeklySchedule, [day]: {...times, startTime: e.target.value, endTime: times.endTime || scheduleEndAfterHour(e.target.value)}})}>
                    <option value="">Select Start Time</option>
                    {generateTimeOptions().map(time => <option key={time.value} value={time.value}>{time.display}</option>)}
                  </select>
                  <span>to</span>
                  <select value={times.endTime} onChange={(e) => setWeeklySchedule({...weeklySchedule, [day]: {...times, endTime: e.target.value}})}>
                    <option value="">Select End Time</option>
                    {generateTimeOptions().map(time => <option key={time.value} value={time.value}>{time.display}</option>)}
                  </select>
                </div>
              ))}
              <button onClick={saveWeeklySchedule} style={{ marginTop: '1rem', padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Weekly Schedule</button>
              {message && (
                <div style={{ textAlign: 'center', marginTop: '0.5rem', padding: '0.5rem', color: isError ? 'red' : 'green', fontWeight: 'bold' }}>
                  {message}
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <h4 style={{ margin: '0 0 0.5rem 0', flexShrink: 0 }}>Schedule Overrides</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto', gap: '0.5rem', marginBottom: '0.75rem', flexShrink: 0 }}>
                <label>Start Date</label>
                <label>Start Time</label>
                <label>End Date</label>
                <label>End Time</label>
                <label>Type</label>
                <div></div>
                <input type="date" value={newOverride.startDate} onChange={(e) => setNewOverride({...newOverride, startDate: e.target.value, endDate: e.target.value || newOverride.endDate})} />
                <select value={newOverride.startTime} onChange={(e) => {
                  const startTime = e.target.value;
                  const end = newOverride.startDate && startTime ? scheduleEndAfterHour(`${newOverride.startDate}T${startTime}`) : '';
                  setNewOverride({ ...newOverride, startTime,
                    ...(!newOverride.endTime ? { endTime: end ? end.slice(11) : scheduleEndAfterHour(startTime), endDate: end ? end.slice(0, 10) : newOverride.endDate } : {})
                  });
                }}>
                  <option value="">Start Time</option>
                  {generateTimeOptions().map(time => <option key={time.value} value={time.value}>{time.display}</option>)}
                </select>
                <input type="date" value={newOverride.endDate} onChange={(e) => setNewOverride({...newOverride, endDate: e.target.value})} />
                <select value={newOverride.endTime} onChange={(e) => setNewOverride({...newOverride, endTime: e.target.value})}>
                  <option value="">End Time</option>
                  {generateTimeOptions().map(time => <option key={time.value} value={time.value}>{time.display}</option>)}
                </select>
                <select value={newOverride.overrideType} onChange={(e) => setNewOverride({...newOverride, overrideType: e.target.value})}>
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
                <button onClick={addOverride} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add</button>
              </div>
              
              <div style={{ overflowY: 'auto', minHeight: 0, maxHeight: '200px', flex: '0 1 200px' }}>
              {overrides.map((override, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', backgroundColor: '#f8f9fa', marginBottom: '0.25rem', borderRadius: '4px' }}>
                  {editingOverride === index ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.5rem', flex: 1 }}>
                      <input type="date" value={override.startDateTime ? override.startDateTime.split('T')[0] : ''} onChange={(e) => updateOverride(index, 'startDate', e.target.value)} />
                      <select value={override.startDateTime && override.startDateTime.split('T')[1] ? override.startDateTime.split('T')[1].slice(0, 5) : ''} onChange={(e) => updateOverride(index, 'startTime', e.target.value)}>
                        <option value="">Start Time</option>
                        {generateTimeOptions().map(time => <option key={time.value} value={time.value}>{time.display}</option>)}
                      </select>
                      <input type="date" value={override.endDateTime ? override.endDateTime.split('T')[0] : ''} onChange={(e) => updateOverride(index, 'endDate', e.target.value)} />
                      <select value={override.endDateTime && override.endDateTime.split('T')[1] ? override.endDateTime.split('T')[1].slice(0, 5) : ''} onChange={(e) => updateOverride(index, 'endTime', e.target.value)}>
                        <option value="">End Time</option>
                        {generateTimeOptions().map(time => <option key={time.value} value={time.value}>{time.display}</option>)}
                      </select>
                      <select value={override.overrideType} onChange={(e) => updateOverride(index, 'overrideType', e.target.value)}>
                        <option value="on">On</option>
                        <option value="off">Off</option>
                      </select>
                    </div>
                  ) : (
                    <span>{(() => {
                      // Display local times
                      if (!override.startDateTime || !override.endDateTime) return 'Invalid date';
                      
                      const startParts = override.startDateTime.split('T');
                      const endParts = override.endDateTime.split('T');
                      if (!startParts[1] || !endParts[1]) return 'Invalid time format';
                      const startDate = startParts[0];
                      const startTime = startParts[1].slice(0, 5);
                      const endDate = endParts[0];
                      const endTime = endParts[1].slice(0, 5);
                      
                      const formatDate = (dateStr) => {
                        const [year, month, day] = dateStr.split('-');
                        return `${parseInt(month)}/${parseInt(day)}/${year}`;
                      };
                      
                      const formatTime = (timeStr) => {
                        const [hour, minute] = timeStr.split(':').map(Number);
                        const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                        const ampm = hour < 12 ? 'AM' : 'PM';
                        return `${h12}:${minute.toString().padStart(2, '0')} ${ampm}`;
                      };
                      
                      return `${formatDate(startDate)} ${formatTime(startTime)} - ${formatDate(endDate)} ${formatTime(endTime)} (${override.overrideType})`;
                    })()}</span>
                  )}
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    {editingOverride === index ? (
                      <button onClick={() => setEditingOverride(null)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✓</button>
                    ) : (
                      <button onClick={() => setEditingOverride(index)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✏️</button>
                    )}
                    <button onClick={() => removeOverride(index)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>🗑️</button>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showPasswordDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Change My Password</h3>
            
            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: '1rem' }}>
                <PasswordField
                  label="Current Password:"
                  value={passwordData.currentPassword} 
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})} 
                  required 
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <PasswordField
                  label="New Password:"
                  value={passwordData.newPassword} 
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} 
                  required 
                />
                <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
                  Password must contain:
                  <ul style={{ margin: '0.25rem 0', paddingLeft: '1rem' }}>
                    <li>At least 10 characters</li>
                    <li>One uppercase letter</li>
                    <li>One lowercase letter</li>
                    <li>One number</li>
                    <li>One special character</li>
                  </ul>
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <PasswordField
                  label="Confirm Password:"
                  value={passwordData.confirmPassword} 
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} 
                  required 
                />
              </div>
              
              {passwordMessage && (
                <div style={{ textAlign: 'center', marginBottom: '1rem', padding: '0.5rem', color: passwordError ? 'red' : 'green', fontWeight: 'bold' }}>
                  {passwordMessage}
                </div>
              )}
              
              <div style={{ textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button type="submit" style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Change Password</button>
                <button type="button" onClick={() => { setShowPasswordDialog(false); setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' }); setPasswordMessage(''); }} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showMyInfoDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ position: 'relative', backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '600px', height: '500px', display: 'flex', flexDirection: 'column' }}>
            {showGoogleCalendarGearOverlay && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: '8px', pointerEvents: 'none' }}>
                <div style={{ animation: 'google-calendar-gear-spin 1s linear infinite', fontSize: '3rem' }}>⚙️</div>
                <style>{`@keyframes google-calendar-gear-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            <div style={{ position: 'relative', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>My Information</h3>
              <button onClick={() => setShowMyInfoDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666', zIndex: 20 }}>×</button>
            </div>
            
            <div style={{ display: 'flex', overflowX: 'auto', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setMyInfoActiveTab('basic')} style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: myInfoActiveTab === 'basic' ? '#e6f3ff' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: myInfoActiveTab === 'basic' ? '1px solid #e6f3ff' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: myInfoActiveTab === 'basic' ? 'bold' : 'normal',
                marginRight: '2px'
              }}>Basic Info</button>
              <button type="button" onClick={() => setMyInfoActiveTab('lessons')} style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: myInfoActiveTab === 'lessons' ? '#f0f8f0' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: myInfoActiveTab === 'lessons' ? '1px solid #f0f8f0' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: myInfoActiveTab === 'lessons' ? 'bold' : 'normal',
                marginRight: '2px'
              }}>Lessons</button>
              <button type="button" onClick={() => { setMyInfoActiveTab('reservations'); if (userInfo?.id) loadUserReservations(userInfo.id); }} style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: myInfoActiveTab === 'reservations' ? '#e6f7ff' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: myInfoActiveTab === 'reservations' ? '1px solid #e6f7ff' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: myInfoActiveTab === 'reservations' ? 'bold' : 'normal'
              }}>Reservations</button>
              <button type="button" onClick={() => { setMyInfoActiveTab('questions'); loadMyPopupQuestionAnswers(); }} style={{
                padding: '0.75rem 1.25rem',
                backgroundColor: myInfoActiveTab === 'questions' ? '#edf6f8' : '#e9ecef',
                color: '#495057',
                border: '1px solid #ccc',
                borderBottom: myInfoActiveTab === 'questions' ? '1px solid #edf6f8' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: myInfoActiveTab === 'questions' ? 'bold' : 'normal',
                whiteSpace: 'nowrap'
              }}>Questions</button>
              {currentUser?.sessionId && (
                <button type="button" onClick={() => setMyInfoActiveTab('calendar')} style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: myInfoActiveTab === 'calendar' ? '#e7f3ff' : '#e9ecef', 
                  color: '#495057',
                  border: '1px solid #ccc', 
                  borderBottom: myInfoActiveTab === 'calendar' ? '1px solid #e7f3ff' : '1px solid #ccc',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: myInfoActiveTab === 'calendar' ? 'bold' : 'normal',
                  marginLeft: '2px'
                }}>Google Calendar</button>
              )}
            </div>
            
            <div style={{ height: '350px', overflow: 'auto', backgroundColor: myInfoActiveTab === 'lessons' ? '#f0f8f0' : '#e6f3ff', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              {myInfoActiveTab === 'basic' && editableUserInfo && (
                <div>
                  <div style={{ marginBottom: '1.25rem', display: 'flex', gap: '1rem', alignItems: 'center', paddingBottom: '1rem', borderBottom: '1px solid #ccc' }}>
                    <div style={{ width: '110px', height: '110px', borderRadius: '50%', border: '1px solid #bbb', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                      {profileImagePreview ? (
                        <button type="button" onClick={() => setShowEnlargedProfileImage(true)} title="View full-size profile image" style={{ padding: 0, border: 'none', backgroundColor: 'transparent', cursor: 'zoom-in', width: '100%', height: '100%' }}>
                          <img src={profileImagePreview} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        </button>
                      ) : (
                        <span style={{ fontSize: '2rem' }}>👤</span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <input type="file" accept="image/png,image/jpeg" onChange={(event) => selectProfileImage(event.target.files[0])} />
                      <div style={{ color: '#6c757d', fontSize: '0.8rem', marginTop: '0.35rem' }}>PNG or JPEG, maximum 1 MB</div>
                      {profileImageFile && <div style={{ color: '#155724', fontSize: '0.8rem', marginTop: '0.5rem' }}>Image will upload with Save Changes.</div>}
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>First Name:</label>
                    <input type="text" value={editableUserInfo.firstName || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, firstName: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Last Name:</label>
                    <input type="text" value={editableUserInfo.lastName || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, lastName: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Email:</label>
                    <input type="email" value={editableUserInfo.email || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, email: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Phone:</label>
                    <input type="tel" value={editableUserInfo.phone || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, phone: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Address 1:</label>
                    <input type="text" value={editableUserInfo.address1 || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, address1: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Address 2:</label>
                    <input type="text" value={editableUserInfo.address2 || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, address2: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>City:</label>
                    <input type="text" value={editableUserInfo.city || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, city: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>State:</label>
                    <input type="text" value={editableUserInfo.state || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, state: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>ZIP:</label>
                    <input type="text" value={editableUserInfo.zip || ''} onChange={(e) => setEditableUserInfo({...editableUserInfo, zip: e.target.value})} style={{ width: '200px', padding: '0.25rem' }} />
                  </div>
                  {userInfoMessage && (
                    <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: userInfoMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: userInfoMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${userInfoMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                      {userInfoMessage}
                    </div>
                  )}
                  <div style={{ textAlign: 'center' }}>
                    <button onClick={saveUserInfo} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Save Changes</button>
                  </div>
                </div>
              )}
              
              {myInfoActiveTab === 'lessons' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0 }}>Lessons</h4>
                    <LessonHistoryPdf key={`${currentUser?.user?.id ?? currentUser?.id}-${currentUser?.user?.orgId}-${currentUser?.sessionId}`} sessionId={currentUser?.sessionId} />
                  </div>
                  {userLessons.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#666' }}>No lessons found</div>
                  ) : (
                    <div>
                      <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                        <strong>Balance: ${userInfo?.balance || 0}</strong>
                      </div>
                      <div style={{ overflow: 'auto' }}>
                        <table style={{ minWidth: '1000px', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem', tableLayout: 'fixed' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Instructor</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Flight Time</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Simulator</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Purchases</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Fee</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Payment</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Credit</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '300px' }}>Debrief</th>
                              <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '300px' }}>Next Flight</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userLessons.map((lesson, index) => (
                              <tr key={index}>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{people && people.find(p => p.id === lesson.instructorId)?.firstName || ''}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.flightTime || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.simulator || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.dualReceived || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.ground || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${lesson.purchases || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${lesson.fee || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${lesson.payment || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${lesson.credit || 0}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem', width: '300px', wordWrap: 'break-word' }}>{lesson.debrief || ''}</td>
                                <td style={{ border: '1px solid #ddd', padding: '0.5rem', width: '300px', wordWrap: 'break-word' }}>{lesson.nextFlight || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{userLessons.reduce((s, l) => s + (parseFloat(l.flightTime) || 0), 0).toFixed(1)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{userLessons.reduce((s, l) => s + (parseFloat(l.simulator) || 0), 0).toFixed(1)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{userLessons.reduce((s, l) => s + (parseFloat(l.dualReceived) || 0), 0).toFixed(1)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{userLessons.reduce((s, l) => s + (parseFloat(l.ground) || 0), 0).toFixed(1)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${userLessons.reduce((s, l) => s + (parseFloat(l.purchases) || 0), 0).toFixed(2)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${userLessons.reduce((s, l) => s + (parseFloat(l.fee) || 0), 0).toFixed(2)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${userLessons.reduce((s, l) => s + (parseFloat(l.payment) || 0), 0).toFixed(2)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${userLessons.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0).toFixed(2)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {myInfoActiveTab === 'reservations' && (
                <div>
                  <h4>Reservations</h4>
                  {userReservations.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#666' }}>No reservations found</div>
                  ) : (
                    <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                      <table style={{ minWidth: '1200px', width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', minWidth: '112px' }}>Time</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Aircraft</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Instructor</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Type</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Status</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Comment</th>
                            <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Maneuvers</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userReservations.map((reservation, index) => (
                            <tr key={reservation.id || index}>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {reservation.startDate ? new Date(reservation.startDate.split('T')[0] + 'T00:00:00').toLocaleDateString() : ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {formatReservationTime(reservation.startTime)} - {formatReservationTime(reservation.endTime)}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {reservation.aircraft ? `${reservation.aircraft.make} ${reservation.aircraft.model} ${reservation.aircraft.tailNumber}` : ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {reservation.instructor ? `${reservation.instructor.firstName} ${reservation.instructor.lastName}` : ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                {reservation.client ? `${reservation.client.firstName} ${reservation.client.lastName}` : ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                <span style={{ 
                                  padding: '0.25rem 0.5rem', 
                                  borderRadius: '4px', 
                                  fontSize: '0.7rem',
                                  backgroundColor: reservation.type === 'lesson' ? '#e3f2fd' : reservation.type === 'solo' ? '#f3e5f5' : '#fff3e0',
                                  color: reservation.type === 'lesson' ? '#1565c0' : reservation.type === 'solo' ? '#7b1fa2' : '#ef6c00'
                                }}>
                                  {reservation.type || 'N/A'}
                                </span>
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                                <span style={{ 
                                  padding: '0.25rem 0.5rem', 
                                  borderRadius: '4px', 
                                  fontSize: '0.7rem',
                                  backgroundColor: reservation.status === 'active' ? '#d4edda' : reservation.status === 'completed' ? '#cce5ff' : '#f8d7da',
                                  color: reservation.status === 'active' ? '#155724' : reservation.status === 'completed' ? '#004085' : '#721c24'
                                }}>
                                  {reservation.status || 'N/A'}
                                </span>
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem', maxWidth: '200px', wordWrap: 'break-word' }}>
                                {reservation.comment || ''}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem', maxWidth: '200px', wordWrap: 'break-word' }}>
                                {reservation.maneuvers ? Array.isArray(reservation.maneuvers) ? reservation.maneuvers.map(m => m.name).join(', ') : reservation.maneuvers : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {myInfoActiveTab === 'questions' && (
                <div>
                  <h4 style={{ marginTop: 0, color: '#1c4b5d' }}>Answered Practice Questions</h4>
                  {myPopupQuestionAnswersError && (
                    <div role="alert" style={{ padding: '0.65rem 0.8rem', marginBottom: '1rem', color: '#721c24', backgroundColor: '#f8d7da', borderRadius: '5px' }}>{myPopupQuestionAnswersError}</div>
                  )}
                  {isLoadingMyPopupQuestionAnswers ? (
                    <p style={{ color: '#687982' }}>Loading answered questions…</p>
                  ) : myPopupQuestionAnswers.length === 0 ? (
                    <p style={{ color: '#687982' }}>No answered questions yet.</p>
                  ) : (
                    myPopupQuestionAnswers.map((item) => {
                      const answerLabel = item.answerType === 'multiple_choice'
                        ? item[`answerChoice${item.userAnswer}`] || `Choice ${item.userAnswer || ''}`
                        : item.userAnswer || 'No answer submitted';
                      return (
                        <section key={item.id} style={{ padding: '0.9rem 0', borderTop: '1px solid #d4e1e6' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem', color: '#60737d', fontSize: '0.8rem' }}>
                            <span>{item.trainingLevel || 'Practice question'}</span>
                            <time>{item.answeredDate ? new Date(item.answeredDate).toLocaleString() : ''}</time>
                          </div>
                          <p style={{ margin: '0 0 0.5rem', fontWeight: 600, lineHeight: 1.45 }}>{item.question || item.title}</p>
                          <p style={{ margin: '0 0 0.5rem' }}><strong>Your answer:</strong> {answerLabel}</p>
                          <div style={{ padding: '0.65rem 0.75rem', color: item.isCorrect === true ? '#285b3e' : item.isCorrect === false ? '#842029' : '#526671', backgroundColor: item.isCorrect === true ? '#edf7ef' : item.isCorrect === false ? '#fbf0f0' : '#f3f6f7', borderRadius: '5px' }}>
                            <strong>{item.isCorrect === true ? 'Correct' : item.isCorrect === false ? 'Not quite' : 'Not graded'}</strong>
                            {item.correctAnswer && <p style={{ margin: '0.4rem 0 0', lineHeight: 1.45 }}><strong>Correct answer:</strong> {item.correctAnswer}</p>}
                            {item.answerFeedback && <p style={{ margin: '0.4rem 0 0', lineHeight: 1.45 }}>{item.answerFeedback}</p>}
                          </div>
                          {item.instructorFeedback && <p style={{ margin: '0.6rem 0 0', padding: '0.65rem 0.75rem', color: '#285b3e', backgroundColor: '#f0f7f3', lineHeight: 1.45 }}><strong>Instructor feedback:</strong> {item.instructorFeedback}</p>}
                        </section>
                      );
                    })
                  )}
                </div>
              )}

              {myInfoActiveTab === 'calendar' && currentUser?.sessionId && (
                <div>
                  <h4 style={{ marginTop: 0 }}>Google Calendar</h4>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#6c757d' }}>Connect your Google account to automatically sync your flight reservations to Google Calendar.</p>
                  {googleCalendarMessage && (
                    <div style={{ marginBottom: '0.75rem', padding: '0.75rem', borderRadius: '4px', backgroundColor: googleCalendarMessageType === 'error' ? '#f8d7da' : '#d4edda', color: googleCalendarMessageType === 'error' ? '#721c24' : '#155724', border: `1px solid ${googleCalendarMessageType === 'error' ? '#f5c6cb' : '#c3e6cb'}` }}>
                      {googleCalendarMessage}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: '600' }}>{googleCalendarStatus.connected ? 'Connected' : 'Not connected'}</span>
                    {googleCalendarStatus.connected && (
                      <span style={{ color: '#6c757d' }}>Sync: {googleCalendarStatus.syncEnabled ? 'Enabled' : 'Disabled'}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: googleCalendarStatus.connected ? '0.75rem' : 0 }}>
                    {!googleCalendarStatus.connected ? (
                      <button onClick={handleGoogleCalendarConnect} disabled={googleCalendarLoading} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: googleCalendarLoading ? 'not-allowed' : 'pointer', opacity: googleCalendarLoading ? 0.7 : 1 }}>
                        {googleCalendarLoading ? 'Working...' : 'Connect Google Calendar'}
                      </button>
                    ) : (
                      <button onClick={handleGoogleCalendarDisconnect} disabled={googleCalendarLoading} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: googleCalendarLoading ? 'not-allowed' : 'pointer', opacity: googleCalendarLoading ? 0.7 : 1 }}>
                        {googleCalendarLoading ? 'Working...' : 'Disconnect'}
                      </button>
                    )}
                    <button onClick={() => handleGoogleCalendarToggle(!googleCalendarStatus.syncEnabled)} disabled={googleCalendarLoading || !googleCalendarStatus.connected} style={{ padding: '0.5rem 0.75rem', backgroundColor: googleCalendarStatus.syncEnabled ? '#28a745' : '#ffc107', color: googleCalendarStatus.syncEnabled ? 'white' : '#212529', border: 'none', borderRadius: '4px', cursor: (googleCalendarLoading || !googleCalendarStatus.connected) ? 'not-allowed' : 'pointer', opacity: (googleCalendarLoading || !googleCalendarStatus.connected) ? 0.7 : 1 }}>
                      {googleCalendarStatus.syncEnabled ? 'Disable Sync' : 'Enable Sync'}
                    </button>
                    <button onClick={handleGoogleCalendarSyncNow} disabled={googleCalendarLoading || !googleCalendarStatus.connected} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: (googleCalendarLoading || !googleCalendarStatus.connected) ? 'not-allowed' : 'pointer', opacity: (googleCalendarLoading || !googleCalendarStatus.connected) ? 0.7 : 1 }}>
                      Sync Now
                    </button>
                    <button onClick={fetchGoogleCalendarStatus} disabled={googleCalendarLoading} style={{ padding: '0.5rem 0.75rem', backgroundColor: '#fff', color: '#495057', border: '1px solid #ccc', borderRadius: '4px', cursor: googleCalendarLoading ? 'not-allowed' : 'pointer', opacity: googleCalendarLoading ? 0.7 : 1 }}>
                      Refresh Status
                    </button>
                  </div>
                  {googleCalendarStatus.connected && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                      <label style={{ fontWeight: '500' }}>Calendar ID:</label>
                      <input
                        type="text"
                        value={calendarIdInput}
                        onChange={(e) => setCalendarIdInput(e.target.value)}
                        placeholder="primary"
                        style={{ padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '220px' }}
                      />
                      <button onClick={handleGoogleCalendarSaveCalendarIdClick} disabled={googleCalendarLoading} style={{ padding: '0.4rem 0.75rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: googleCalendarLoading ? 'not-allowed' : 'pointer', opacity: googleCalendarLoading ? 0.7 : 1 }}>
                        Save
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowMyInfoDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {showMyLessonsDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '800px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#ffe6f0', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>My Lessons Taught</h3>
              <button onClick={() => setShowMyLessonsDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ height: '500px', overflow: 'auto', backgroundColor: '#ffe6f0', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <h4>Lessons Taught (Count: {myWorkLessons.length})</h4>
                {workLessonMessage && (
                  <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: workLessonMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: workLessonMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${workLessonMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                    {workLessonMessage}
                  </div>
                )}
              </div>
              
              <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Actions</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Start Time</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client Name</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground Instruction</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Earning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myWorkLessons.map((lesson, index) => {
                      const isEditing = editingWorkLessonId === lesson.id;
                      return (
                        <tr key={lesson.id || index}>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? (
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <button onClick={() => saveMyWorkLesson(lesson)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                <button onClick={() => setEditingWorkLessonId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                              </div>
                            ) : (
                              <button onClick={() => setEditingWorkLessonId(lesson.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                            )}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="date" value={lesson.date || ''} onChange={(e) => updateWorkLessonField(lesson.id, 'date', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="text" value={lesson.startTime || ''} onChange={(e) => updateWorkLessonField(lesson.id, 'startTime', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.startTime || '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? (
                              <select value={lesson.clientId || ''} onChange={(e) => {
                                const clientId = e.target.value;
                                const clientName = getClientName(parseInt(clientId));
                                updateWorkLessonField(lesson.id, 'clientId', clientId);
                                updateWorkLessonField(lesson.id, 'clientName', clientName);
                              }} style={{ width: '100%', border: 'none' }}>
                                <option value="">None</option>
                                {activeClients.map(client => (
                                  <option key={client.id} value={client.id}>{client.firstName} {client.lastName}</option>
                                ))}
                              </select>
                            ) : (
                              getClientName(lesson.clientId, lesson.clientName)
                            )}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="number" step="0.1" value={lesson.dualGiven || ''} onChange={(e) => updateWorkLessonField(lesson.id, 'dualGiven', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.dualGiven || '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="number" step="0.1" value={lesson.groundInstruction || ''} onChange={(e) => updateWorkLessonField(lesson.id, 'groundInstruction', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.groundInstruction || '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="number" step="0.1" value={lesson.total || ''} onChange={(e) => updateWorkLessonField(lesson.id, 'total', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (lesson.total || '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            ${(((parseFloat(lesson.dualGiven) || 0) + (parseFloat(lesson.groundInstruction) || 0)) * ((currentUser?.user?.payRate || currentUser?.payRate) || 0)).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowMyLessonsDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {showMyExpensesDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '800px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#fff0e6', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>My Expenses</h3>
              <button onClick={() => setShowMyExpensesDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ height: '500px', overflow: 'auto', backgroundColor: '#fff0e6', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4>Expenses (Count: {myExpenses.length})</h4>
                  <button onClick={addNewMyExpense} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add New</button>
                </div>
                {expenseMessage && (
                  <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: expenseMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: expenseMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${expenseMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                    {expenseMessage}
                  </div>
                )}
              </div>
              
              <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Actions</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expense</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Aircraft</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Details</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myExpenses.map((expense, index) => {
                      const isEditing = editingExpenseId === expense.id;
                      return (
                        <tr key={expense.id || index}>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              {isEditing ? (
                                <>
                                  <button onClick={() => saveMyExpense(expense)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                  <button onClick={() => setEditingExpenseId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => setEditingExpenseId(expense.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                  {expense.id && !expense.id.toString().startsWith('temp_') && <button onClick={() => deleteMyExpense(expense.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>🗑️</button>}
                                </>
                              )}
                            </div>
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="date" value={expense.date || ''} onChange={(e) => updateMyExpenseField(expense.id, 'date', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.date ? new Date(expense.date + 'T00:00:00').toLocaleDateString() : '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="text" value={expense.expense || ''} onChange={(e) => updateMyExpenseField(expense.id, 'expense', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.expense || '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="text" value={expense.vendor || ''} onChange={(e) => updateMyExpenseField(expense.id, 'vendor', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.vendor || '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? (
                              <select value={expense.aircraftId || ''} onChange={(e) => updateMyExpenseField(expense.id, 'aircraftId', e.target.value)} style={{ width: '100%', border: 'none' }}>
                                <option value="">Select Aircraft</option>
                                {aircraft.map(plane => (
                                  <option key={plane.id} value={plane.id}>{plane.make || plane.manufacturer} {plane.model} {plane.tailNumber || plane.registration}</option>
                                ))}
                              </select>
                            ) : (
                              (() => {
                                const expenseAircraft = aircraft.find(a => a.id === expense.aircraftId);
                                return expenseAircraft ? `${expenseAircraft.make || expenseAircraft.manufacturer || ''} ${expenseAircraft.model || ''} ${expenseAircraft.tailNumber || expenseAircraft.registration || ''}`.trim() : '';
                              })()
                            )}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="text" value={expense.details || ''} onChange={(e) => updateMyExpenseField(expense.id, 'details', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.details || '')}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? <input type="number" step="0.01" value={expense.total || ''} onChange={(e) => updateMyExpenseField(expense.id, 'total', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${expense.total || 0}`}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                            {isEditing ? (
                              <div>
                                <input
                                  type="file"
                                  aria-label="Choose receipt file"
                                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.target.value = '';
                                    if (file) handleMyExpenseReceiptChange(expense.id, file);
                                  }}
                                  style={{ width: '220px', border: 'none', fontSize: '12px', color: '#333', cursor: 'pointer' }}
                                />
                                <ReceiptExtractionStatus isLoading={extractingExpenseIds.includes(expense.id)} />
                                {expense.receiptFile && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Selected: {expense.receiptFile.name}</div>}
                                {!expense.receiptFile && expense.receiptImage && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Current file: {expense.receiptImage.split('/').pop()}</div>}
                              </div>
                            ) : (
                              (expense.receiptImage || expense.isAttachment) ? (
                                <button
                                  type="button"
                                  aria-label="View receipt"
                                  onClick={() => openMyExpenseReceipt(expense)}
                                  style={{ cursor: 'pointer', fontSize: '16px', backgroundColor: 'transparent', border: 'none', padding: '0.25rem' }}
                                  title="Click to view receipt"
                                >
                                  📎
                                </button>
                              ) : ''
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowMyExpensesDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {showReceiptModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Receipt</h3>
              <button type="button" aria-label="Close receipt" onClick={closeReceiptModal} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center', width: '100%', height: '100%' }}>
              {isLoadingReceipt ? (
                <p role="status">Loading receipt...</p>
              ) : receiptModalMessage ? (
                <p role="alert" style={{ color: '#842029' }}>{receiptModalMessage}</p>
              ) : selectedReceiptType === 'pdf' ? (
                <iframe src={selectedReceiptImage} style={{ width: '100%', height: '75vh', border: 'none' }} title="Receipt PDF" />
              ) : (
                <img src={selectedReceiptImage} alt="Receipt" onError={() => setReceiptModalMessage('Unable to display receipt. The image may be unavailable or access may be denied.')} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}
      
      {showMyClientLessonsDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '900px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#f0f8f0', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>My Lessons</h3>
              <button onClick={() => setShowMyClientLessonsDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ height: '500px', overflow: 'auto', backgroundColor: '#f0f8f0', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              {myClientLessons.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666' }}>No lessons found</div>
              ) : (
                <div>
                  <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                    <strong>Balance: ${myClientUserInfo?.balance || 0}</strong>
                  </div>
                  <div style={{ overflow: 'auto' }}>
                    <table style={{ minWidth: '1000px', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem', tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Flight Time</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Simulator</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Purchases</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Fee</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Credit</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '300px' }}>Debrief</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left', width: '300px' }}>Next Flight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myClientLessons.map((lesson, index) => (
                          <tr key={index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.flightTime || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.simulator || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.dualReceived || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{lesson.ground || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${lesson.purchases || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${lesson.fee || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>${lesson.credit || 0}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem', width: '300px', wordWrap: 'break-word' }}>{lesson.debrief || ''}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem', width: '300px', wordWrap: 'break-word' }}>{lesson.nextFlight || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowMyClientLessonsDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {showMyEarningsDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '800px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>My Earnings Statements</h3>
              <button onClick={() => setShowMyEarningsDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ height: '500px', overflow: 'auto', backgroundColor: '#e6f7ff', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <h4>Earnings Statements (Count: {myEarningsStatements.length})</h4>
                {myEarningsStatements.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>No earnings statements found</div>
                )}
              </div>
              
              <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Start Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>End Date</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expenses</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Hours Earned</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Hours Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(myEarningsStatements || []).map((earnings, index) => (
                      <tr key={earnings.id || index} onClick={() => handleEarningsStatementClick(earnings)} style={{ cursor: 'pointer' }}>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {earnings.startDate ? new Date(earnings.startDate + 'T00:00:00').toLocaleDateString() : ''}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {earnings.endDate ? new Date(earnings.endDate + 'T00:00:00').toLocaleDateString() : ''}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          ${earnings.total || 0}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          ${earnings.expenses || 0}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {earnings.rentalHoursEarned || 0}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                          {earnings.rentalHoursUsed || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowMyEarningsDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {showEarningsDetailsDialog && selectedEarningsStatement && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '900px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>Earnings Statement Details ({selectedEarningsStatement.startDate} - {selectedEarningsStatement.endDate})</h3>
              <button onClick={() => setShowEarningsDetailsDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setEarningsDetailsActiveTab('workLessons')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDetailsActiveTab === 'workLessons' ? '#ffe6f0' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDetailsActiveTab === 'workLessons' ? '1px solid #ffe6f0' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDetailsActiveTab === 'workLessons' ? 'bold' : 'normal',
                marginRight: '2px',
                fontSize: '0.8rem'
              }}>Work Lessons</button>
              <button type="button" onClick={() => setEarningsDetailsActiveTab('expenses')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDetailsActiveTab === 'expenses' ? '#fff0e6' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDetailsActiveTab === 'expenses' ? '1px solid #fff0e6' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDetailsActiveTab === 'expenses' ? 'bold' : 'normal',
                fontSize: '0.8rem'
              }}>Expenses</button>
            </div>
            
            <div style={{ height: '450px', overflow: 'auto', backgroundColor: earningsDetailsActiveTab === 'workLessons' ? '#ffe6f0' : '#fff0e6', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #ddd', textAlign: 'center' }}>
                <strong>Available Rental Hours:</strong> {(selectedEarningsStatement.rentalHoursEarned || 0) - (selectedEarningsStatement.rentalHoursUsed || 0)} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <strong>Rental Hours Earned:</strong> {selectedEarningsStatement.rentalHoursEarned || 0} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <strong>Rental Hours Used:</strong> {selectedEarningsStatement.rentalHoursUsed || 0}
              </div>
              {earningsDetailsActiveTab === 'workLessons' && (
                <div>
                  <h4>Work Lessons (Count: {earningsWorkLessons.length})</h4>
                  <div style={{ overflow: 'auto', maxHeight: '350px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Start Time</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client Name</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground Instruction</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {earningsWorkLessons.map((lesson, index) => (
                          <tr key={lesson.id || index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.startTime || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.clientName || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.dualGiven || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.groundInstruction || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.total || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {earningsDetailsActiveTab === 'expenses' && (
                <div>
                  <h4>Expenses (Count: {earningsExpenses.length})</h4>
                  <div style={{ overflow: 'auto', maxHeight: '350px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expense</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Details</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {earningsExpenses.map((expense, index) => (
                          <tr key={expense.id || index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.date ? new Date(expense.date + 'T00:00:00').toLocaleDateString() : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.expense || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.vendor || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.details || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              ${expense.total || 0}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.isAttachment ? (
                                <span 
                                  onClick={async () => {
                                    try {
                                      const response = await fetch(`${API_BASE_URL}/expense/${expense.id}/image`, {
                                        headers: {
                                          'session-id': currentUser?.sessionId
                                        }
                                      });
                                      if (response.ok) {
                                        const blob = await response.blob();
                                        const imageUrl = URL.createObjectURL(blob);
                                        setSelectedReceiptImage(imageUrl);
                                        setShowReceiptModal(true);
                                      }
                                    } catch (error) {
                                      console.error('Error loading receipt image:', error);
                                    }
                                  }}
                                  style={{ cursor: 'pointer', fontSize: '16px' }}
                                  title="Click to view receipt"
                                >
                                  📎
                                </span>
                              ) : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowEarningsDetailsDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {showMyProgressDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '800px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#f0f0f8', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>My Training Progress</h3>
              <button onClick={() => setShowMyProgressDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ height: '500px', overflow: 'auto', backgroundColor: '#f0f0f8', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0 }}>Training Progress</h4>
                <select value={myProgressCategoryFilter} onChange={(event) => selectMyProgressCategory(event.target.value)} style={{ padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '180px' }}>
                  {myProgressCategories.length === 0 ? <option value="">No categories</option> : myProgressCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
                <select value={myProgressStageFilter} onChange={(event) => setMyProgressStageFilter(event.target.value)} disabled={!myProgressCategoryFilter || myProgressStages.length === 0} style={{ padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '180px', opacity: (!myProgressCategoryFilter || myProgressStages.length === 0) ? 0.6 : 1 }}>
                  {myProgressStages.length === 0 ? <option value="">No stages</option> : myProgressStages.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                </select>
              </div>
              {myProgressMessage && (
                <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: myProgressMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: myProgressMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${myProgressMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                  {myProgressMessage}
                </div>
              )}
              {(Array.isArray(myProgressData) ? myProgressData : []).length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666' }}>No progress data found</div>
              ) : (
                <div style={{ overflow: 'auto', maxHeight: '400px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Requirement</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Category</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Stage</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Status</th>
                        <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMyProgressData.length === 0 ? (
                        <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No progress data found for the selected filters</td></tr>
                      ) : filteredMyProgressData.map((item, index) => {
                        const level = parseInt(item.level);
                        const backgroundColor = level >= 4 ? '#e8f5e8' : level === 3 ? '#e8f4fd' : 'white';
                        return (
                          <tr key={item.id || index} style={{ backgroundColor }}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{item.requirement || ''}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{item.category || ''}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{item.stage || ''}</td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              <span style={{ 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '4px', 
                                fontSize: '0.7rem',
                                backgroundColor: item.status === 'completed' ? '#d4edda' : item.status === 'in_progress' ? '#fff3cd' : '#f8d7da',
                                color: item.status === 'completed' ? '#155724' : item.status === 'in_progress' ? '#856404' : '#721c24'
                              }}>
                                {item.status || 'Not Started'}
                              </span>
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{item.level || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowMyProgressDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      <UserFeatureRequestsDialog 
        showDialog={showUserFeatureRequestsDialog}
        onClose={() => setShowUserFeatureRequestsDialog(false)}
      />
      
      {showDeleteAccountDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 8px 16px rgba(0,0,0,0.2)', maxWidth: '500px', width: '90%', border: '2px solid #dc3545' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
              <h3 style={{ margin: '0 0 1rem 0', color: '#dc3545', fontSize: '1.5rem' }}>Delete Account</h3>
              <p style={{ margin: '0', color: '#666', lineHeight: '1.6' }}>
                Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed.
              </p>
            </div>
            
            {deleteAccountMessage && (
              <div style={{ textAlign: 'center', marginBottom: '1rem', padding: '0.75rem', color: deleteAccountMessage.includes('Error') ? '#dc3545' : '#28a745', backgroundColor: deleteAccountMessage.includes('Error') ? '#f8d7da' : '#d4edda', borderRadius: '6px', border: `1px solid ${deleteAccountMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`, fontWeight: 'bold' }}>
                {deleteAccountMessage}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button 
                onClick={async () => {
                  try {
                    const response = await fetch(`${API_BASE_URL}/user/current`, {
                      method: 'DELETE',
                      headers: {
                        'session-id': currentUser?.sessionId
                      }
                    });
                    
                    if (response.ok) {
                      setDeleteAccountMessage('Account deleted successfully. Logging out...');
                      setTimeout(() => {
                        setCurrentUser(null);
                        navigate('/login');
                      }, 2000);
                    } else {
                      const errorText = await response.text();
                      setDeleteAccountMessage(`Error: ${errorText || 'Failed to delete account'}`);
                    }
                  } catch (error) {
                    setDeleteAccountMessage(`Error: ${error.message}`);
                  }
                }}
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: '#dc3545', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Yes, Delete My Account
              </button>
              <button 
                onClick={() => {
                  setShowDeleteAccountDialog(false);
                  setDeleteAccountMessage('');
                }} 
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: '#6c757d', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navigation;